import { create } from 'zustand';

// frontend-restructuring §2.2 (map slice, paso a3) — reemplaza 4 canales de acoplamiento entre
// `Map.jsx` y sus hermanos `SettingsAssets.jsx`/`MapTools.jsx` (antes CustomEvents de `window` +
// una función global `window.spawnMapAsset`). Mismo patrón de referencia que `useShellStore`/
// `useMediaStore`: un store plano, leído con selectores (`useMapBridge((s) => s.campo)`), nunca
// con la llamada sin argumento (`useMapBridge()`).
export const useMapBridge = create((set) => ({
    // edit-map-asset (Map -> SettingsAssets)
    editingAsset: null, // el objeto de la decoración/terminal seleccionada, o null si está cerrado
    openAssetEditor: (asset) => set({ editingAsset: asset }),
    closeAssetEditor: () => set({ editingAsset: null }),

    // update-map-asset-local (SettingsAssets -> Map): comando dirigido a Map, no un valor
    // pasivo -- Map se suscribe con `useMapBridge.subscribe(...)` y reacciona cuando este
    // campo cambia (de ahí el `ts` -- dos ediciones consecutivas al mismo campo/valor deben
    // seguir disparando la suscripción).
    requestAssetFieldUpdate: null,
    requestFieldUpdate: (payload) => set({ requestAssetFieldUpdate: { ...payload, ts: Date.now() } }),

    // active-terminal-change (Map -> MapTools)
    activeTerminalId: null,
    setActiveTerminalId: (id) => set({ activeTerminalId: id ?? null }),

    // window.spawnMapAsset (MapTools -> Map, 7o canal no documentado en el README original).
    // La función real se asigna acá en tiempo de ejecución por `useMapSocketSync.js` al montar
    // (necesita refs que ese hook ya posee -- mapImages/camera/socketRef/spaceSlug), igual que
    // antes se asignaba a `window.spawnMapAsset`, solo que ahora vive en el store en vez de en
    // el objeto global.
    spawnAsset: null,

    decorationsVersion: 0,
    bumpDecorationsVersion: () => set((state) => ({ decorationsVersion: state.decorationsVersion + 1 })),

    // map-groups-update (Map -> MenuMedia, paso a4) -- NO es map-interno, es map -> shell
    // chrome. Se queda en `useMapBridge` (no en `useShellStore`/`useSaveStatusStore`) porque
    // `MenuMedia.jsx` no leía ninguno de esos dos stores antes de este paso -- meterlo acá
    // evita retrofittear un contrato ajeno (ver 02.2-map.md §3 para el porqué completo).
    mapGroups: null, // { groups: [...], isolated: [...] } o null antes del primer map-update
    setMapGroups: (mapGroups) => set({ mapGroups }),

    // global-save-pulse / global-save-pulse-error (Map -> MenuMedia, paso a4). `MenuMedia`
    // sigue con sus propios timers locales (800ms/2000ms) -- este store solo dispara el
    // "evento", MenuMedia decide cuánto dura visualmente, igual que antes con CustomEvents.
    saveStatus: { saving: false, error: false },
    pulseMapSave: () => set({ saveStatus: { saving: true, error: false } }),
    pulseMapSaveError: () => set({ saveStatus: { saving: false, error: true } }),

    // player-moved (Map -> CallOverlay + useLeaderPositionPersistence, 04-enforce.md 4.3, era
    // un `player-moved` window CustomEvent). El game loop de `Map.jsx` llama
    // `notifyPlayerMoved()` cuando la posición del avatar cambió desde la última notificación
    // (no cada frame). Los 2 consumidores solo reinician un timer de debounce -- se suscriben
    // imperativamente con `useMapBridge.subscribe(...)` (sin re-render), no con un selector.
    playerMovedAt: 0,
    notifyPlayerMoved: () => set({ playerMovedAt: Date.now() }),

    // Integración main→dev epic C4: wizard de conexión de consola. El hit-test del botón
    // "conectar console" vive en `useMapCanvasInput` (canvas) y el modal en `Map.jsx` — este
    // slice los conecta. `{ id, name, ip }` mientras está abierto, null si no.
    consoleWizard: null,
    openConsoleWizard: (payload) => set({ consoleWizard: payload }),
    closeConsoleWizard: () => set({ consoleWizard: null }),

    // Integración main→dev epic C5 (`afbfe1fd`): doble-click en un avatar de la barra de
    // proximidad (`MenuMedia`) → caminar hacia ese usuario. `{ x, y, userId, ts }` como comando
    // dirigido (el `ts` re-dispara la suscripción de `useMapTabSync` aunque el destino repita).
    walkToRequest: null,
    requestWalkTo: ({ x, y, userId }) => set({ walkToRequest: { x, y, userId, ts: Date.now() } }),

    // Video projection feature for terminal sessions
    watchedSessionTerminalId: null,
    setWatchedSessionTerminalId: (id) => set({ watchedSessionTerminalId: id }),
    watchedSessionId: null,
    setWatchedSessionId: (id) => set({ watchedSessionId: id }),
    watchedSessionDetails: null,
    setWatchedSessionDetails: (details) => set({ watchedSessionDetails: details }),
    watchedSessionParticipants: [],
    setWatchedSessionParticipants: (participants) => set({ watchedSessionParticipants: participants }),
    remoteCameraStreams: {},
    setRemoteCameraStreams: (streams) => set({ remoteCameraStreams: streams }),
}));
